import { ReactElement, useEffect, useMemo, useState } from 'react'
import {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetStaticPaths,
  GetStaticPathsContext,
  GetStaticPropsContext,
} from 'next'
import Head from 'next/head'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import dayjs from 'dayjs'
import { SwiperSlide } from 'swiper/react'
import { isValidURL } from '@common/uri-helper'
import { NextPageWithLayout } from '@common/common-types'
import retriveMessages, { LocaleType } from '@messages/message'
import PdSwiper from '@components/pd-swiper/pd-swiper'
import PageLayout from '@components/page-layout/page-layout'
import PdImage from '@components/pd-image/pd-image'
import { getInternalNewsDetails, NewsDetailsData } from '@service/news.service'
import { SEOData } from '@service/seo.service'
import AngleArrow from '@components/pd-angle-arrow/pd-angle-arrow'
import AngleRight from '@/components/pd-angle-right/pd-angle-right'
import PdNews from './pd-news.component'
import dynamic from 'next/dynamic'
import { findRegionByLocale } from '@/common/region-locale'
import { formatDate } from '@/common/date'
import { isDataEmpty } from '@/common/misc'
import PdProductList from '@components/pd-product-list/pd-product-list'
import { withSEO } from '@/common/with-seo'
import styles from './news-detail.module.scss'
import { getLinkRelForHref } from '@/common/link-rel-helper'
const RichText = dynamic(() => import('@/components/pd-rich-text'), { ssr: false })

const NewsItem: NextPageWithLayout<{ data: NewsDetailsData; host: string; seoData?: SEOData | null }> = (props) => {
  const { data } = props
  const t = useTranslations()
  const { locale, back } = useRouter()
  console.log('dataBlog', data)

  /** 上一篇文章（左箭头），使用后端返回的 beforeArticle */
  const prevArticle = useMemo(() => {
    if (!data?.beforeArticle) return null
    return {
      link: `/news/${data.beforeArticle.docId}`,
      title: data.beforeArticle.title,
    }
  }, [data])

  /** 下一篇文章（右箭头），取 other 列表中的第一篇 */
  const nextArticle = useMemo(() => {
    if (!data?.other?.length) return null
    const article = data.other[0]
    return {
      link: `/news/${article.docId}`,
      title: article.title,
    }
  }, [data])

  /** Other Blogs 推荐列表，使用 other 全部内容 */
  const MiniNewsList = useMemo(() => {
    if (!data?.other?.length) {
      return []
    }

    return data.other.map((article) => ({
      id: article.docId,
      cover: article.mediaUrl,
      mini: true,
      title: article.title,
      overview: article.secondTitle,
      date: formatDate(article.date, locale),
      link: `/news/${article.docId}`,
      type: article.type,
      typeTitle: article.typeTitle,
    }))
  }, [data, locale])

  const recommendProducts = useMemo(() => {
    return data?.recommendProducts || []
  }, [data])

  const [isMobileDevice, setMobileDevice] = useState<boolean>(false)
  useEffect(() => {
    setMobileDevice(window.innerWidth < 1200)
  }, [])

  const [slidesOffsetBeforePx, setSlidesOffsetBeforePx] = useState<number>(0)

  useEffect(() => {
    setSlidesOffsetBeforePx(isMobileDevice ? 0 : window.innerWidth * 0.5 - 397.44)
  }, [isMobileDevice])

  useEffect(() => {
    if (!data) {
      return
    }

    const contentIsValidHttp = isValidURL(data.content)
    // 如果content是URL,就直接跳转到对应的URL页面
    if (contentIsValidHttp) {
      window.open(data.content, '_blank')
    }
  }, [data, data?.content])

  return (
    <>
      <Head>
        <link rel="stylesheet" type="text/css" href="/css/quill.bubble.css" />
      </Head>
      <div className="w-full bg-white">
        <div className="mx-auto w-full max-w-795px">
          <div className={['pt-0', 'laptop:pt-16', 'flex-col items-center pb-16'].join(' ')}>
            {/* header */}
            <div className="w-full">
              {data?.mediaUrl && (
                <img src={data?.mediaUrl} alt={data?.title || '新闻图片'} className="w-full h-auto" loading="lazy" />
              )}
            </div>

            <div className={['px-6', 'laptop:px-0', 'w-full'].join(' ')}>
              <h1 className={[styles['article-title'], 'pt-6 font-semibold'].join(' ')}>{data?.title}</h1>
              <div className="flex-row mt-6">
                {!!data?.date && <span className="text-lg text-black/60 mr-4">{formatDate(data?.date, locale)}</span>}
                {data?.typeTitle && <span className="text-pdblue capitalize">{data.typeTitle}</span>}
              </div>

              {/* content */}
              {/* <div id="newsContainer" className={'border-t border-b border-gray-300 py-12 mt-6 ql-editor'}></div> */}
              <div className={'border-t border-b border-gray-300 pt-12 pb-16 mt-6'}>
                {isValidURL(data?.content) ? (
                  // eslint-disable-next-line react/jsx-no-target-blank
                  <a
                    style={{ cursor: 'pointer', color: '#007aff', textDecoration: 'underline' }}
                    href={data.content}
                    target="_blank"
                    rel={getLinkRelForHref(data.content, 'noreferrer', '_blank')}
                  >
                    {data?.content}
                  </a>
                ) : (
                  <RichText richText={data?.content} className={styles['article-content']} normalizeTypography />
                )}
                {/* 指定产品推荐 - 仅在 Blogs 类型时显示 */}
                {data?.typeTitle === 'Blogs' && recommendProducts.length > 0 && (
                  <div className="w-full mx-auto bg-white overflow-x-hidden">
                    <div className={['pt-16', 'laptop:px-0 ', ' w-full'].join(' ')}>
                      <div className={'mx-auto w-full max-w-795px mb-6 laptop:mb-8'}>
                        <h2 className={['text-2xl', 'laptop:text-3xl', 'w-full font-semibold '].join(' ')}>
                          {t('RelatedProducts')}
                        </h2>
                      </div>
                      <div className={['mx-auto w-full max-w-795px'].join(' ')}>
                        <PdProductList products={recommendProducts} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* footer - 阅读导航 */}
              <div className="flex justify-between items-center mt-6 mb-8">
                <a
                  className={['text-base', 'laptop:text-lg', 'text-black/60 font-semibold cursor-pointer'].join(' ')}
                  onClick={back}
                >
                  {t('Back')}
                </a>
                <div className="flex items-center gap-6">
                  {/* 上一篇箭头 - 始终显示，无上一篇时置灰 */}
                  {prevArticle ? (
                    <Link href={prevArticle.link}>
                      <a className="flex items-center justify-center text-gray-400 font-semibold cursor-pointer">
                        <AngleArrow />
                      </a>
                    </Link>
                  ) : (
                    <AngleArrow disabled={true} />
                  )}

                  {/* 下一篇箭头 - 始终显示，无下一篇时置灰 */}
                  {nextArticle ? (
                    <Link href={nextArticle.link}>
                      <a className="flex items-center justify-center text-gray-400 font-semibold cursor-pointer">
                        <AngleArrow direction={'right'} />
                      </a>
                    </Link>
                  ) : (
                    <AngleArrow direction={'right'} disabled={true} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 其它新闻 */}
        {!!MiniNewsList.length && (
          <div className="w-full bg-pdbg overflow-x-hidden">
            <div className={['py-15 px-6 laptop:px-0', 'laptop:py-24', 'mx-auto w-full'].join(' ')}>
              <div className={'mx-auto flex justify-between items-center w-full max-w-795px mb-6 laptop:mb-8'}>
                <h2
                  className={['text-2xl', ' laptop:pl-0', 'laptop:text-3xl', 'w-full font-semibold flex-1'].join(' ')}
                >
                  {props.data.typeTitle === 'Blogs' ? t('OtherBlogs') : t('OtherNews')}
                </h2>
                {/* Learn More - PC 端和移动端均显示 */}
                <Link href={'/news'} passHref>
                  <a className="text-blue-600 text-sm laptop:text-base font-normal flex items-center gap-1">
                    <span>{t('LearnMore')}</span>
                    <AngleRight theme="PUDUBLUE" size="4x8" />
                  </a>
                </Link>
              </div>
              <div className="w-full ">
                <PdSwiper
                  className={'!overflow-visible '}
                  slidesPerView={1}
                  spaceBetween={16}
                  breakpoints={{
                    640: { slidesPerView: 1.8, spaceBetween: 16 },
                    768: { slidesPerView: 2.2, spaceBetween: 16 },
                    1024: { slidesPerView: 3.5, spaceBetween: 16 },
                    1280: { slidesPerView: 4.75, spaceBetween: 16 },
                  }}
                  hideNavigation={true}
                  hidePagination={true}
                  slidesOffsetBefore={slidesOffsetBeforePx}
                  onResize={() => !isMobileDevice && setSlidesOffsetBeforePx(window.innerWidth * 0.5 - 397.44)}
                >
                  {MiniNewsList.map((news, index) => (
                    <SwiperSlide
                      key={index}
                      className="laptop:h-390px h-334px laptop:max-w-400px laptop:min-w-400px overflow-hidden"
                    >
                      <PdNews
                        key={index}
                        mini={news.mini}
                        img={news.cover}
                        title={news.title}
                        overview={news.overview}
                        link={news.link}
                        date={news.date}
                        type={news.type}
                        typeTitle={news.typeTitle}
                        imgClass={''}
                      />
                    </SwiperSlide>
                  ))}
                </PdSwiper>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

NewsItem.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'article'}
    >
      {page}
    </PageLayout>
  )
}

export default NewsItem

export const getServerSideProps = withSEO(
  async ({ locale, query, req }: GetServerSidePropsContext) => {
    const messages = retriveMessages(['news'], locale as LocaleType)
    const host = req.headers.host
    const region = findRegionByLocale(locale as string)
    // @ts-ignore
    const data = await getInternalNewsDetails(query.newsId, region)
    const docId = '' + data?.docId

    if (docId === '') {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        data,
        messages,
        host,
        seoData: data?.seoData,
      },
    }
  },
  ({ query }: GetServerSidePropsContext) => {
    const newsId = (query as { newsId: string }).newsId
    return `/news/${newsId}`
  }
)
